import api from './api';
import { CorpusSector, CorpusWord, GlossaryTerm } from '../types';

export interface CorpusWordWithSectors extends CorpusWord {
  sectors: {
    id: number;
    wordId: number;
    sectorId: number;
    relevanceScore: number;
    isTechnical: boolean;
    sector: CorpusSector;
  }[];
}

export interface WordsBySectorResponse {
  success: boolean;
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasMore: boolean;
  words: CorpusWordWithSectors[];
}

export interface CreateWordData {
  word: string;
  normalizedWord: string;
  mcerlLevel: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  syllableCount?: number;
  complexityScore?: number;
  frequencyScore?: number;
  partOfSpeech?: string;
  lemma?: string;
  stem?: string;
  wordType?: 'GENERAL' | 'TECHNICAL' | 'SPECIALIZED';
  sectorIds?: number[];
}

export interface UpdateWordData {
  word?: string;
  normalizedWord?: string;
  mcerlLevel?: 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
  syllableCount?: number;
  complexityScore?: number;
  frequencyScore?: number;
  partOfSpeech?: string;
  lemma?: string;
  stem?: string;
  wordType?: 'GENERAL' | 'TECHNICAL' | 'SPECIALIZED';
  sectorIds?: number[];
}

export interface ImportWordsData {
  sectorId?: number;
  words: CreateWordData[];
}

export interface ImportResultsResponse {
  success: boolean;
  message: string;
  results: {
    created: number;
    skipped: number;
    errors: string[];
  };
}

export interface CorpusStats {
  success: boolean;
  stats: {
    total: number;
    totalGlossary: number;
    totalSectors: number;
    byLevel: {
      mcerlLevel: string;
      count: number;
    }[];
    bySector: {
      sector: CorpusSector;
      count: number;
    }[];
  };
}

export const corpusService = {
  /**
   * Get all sectors
   */
  async getSectors(): Promise<{ sectors: CorpusSector[] }> {
    const response = await api.get<{ sectors: CorpusSector[] }>('/corpus/sectors');
    return response.data;
  },

  /**
   * Get words for a specific sector with pagination
   */
  async getSectorWords(
    sectorId: number,
    params?: {
      search?: string;
      level?: string;
      page?: number;
      limit?: number;
    }
  ): Promise<WordsBySectorResponse> {
    const response = await api.get<WordsBySectorResponse>(`/corpus/sectors/${sectorId}/words`, {
      params,
    });
    return response.data;
  },

  /**
   * Get glossary terms for a specific sector
   */
  async getSectorGlossary(sectorId: number, priority?: string): Promise<{
    terms: GlossaryTerm[];
  }> {
    const response = await api.get<{ terms: GlossaryTerm[] }>(
      `/corpus/sectors/${sectorId}/glossary`,
      { params: { priority } }
    );
    return response.data;
  },

  /**
   * Search words in corpus
   */
  async searchWords(query: string, sectorId?: number, level?: string): Promise<{
    words: CorpusWord[];
  }> {
    const response = await api.get<{ words: CorpusWord[] }>('/corpus/words/search', {
      params: { q: query, sectorId, level },
    });
    return response.data;
  },

  /**
   * Create a new corpus word
   */
  async createWord(data: CreateWordData): Promise<{
    success: boolean;
    message: string;
    word: CorpusWordWithSectors;
  }> {
    const response = await api.post('/corpus/words', data);
    return response.data;
  },

  /**
   * Update a corpus word
   */
  async updateWord(
    id: number,
    data: UpdateWordData
  ): Promise<{
    success: boolean;
    message: string;
    word: CorpusWordWithSectors;
  }> {
    const response = await api.put(`/corpus/words/${id}`, data);
    return response.data;
  },

  /**
   * Delete a corpus word
   */
  async deleteWord(id: number): Promise<{
    success: boolean;
    message: string;
  }> {
    const response = await api.delete(`/corpus/words/${id}`);
    return response.data;
  },

  /**
   * Bulk import corpus words
   */
  async importWords(data: ImportWordsData): Promise<ImportResultsResponse> {
    const response = await api.post<ImportResultsResponse>('/corpus/words/import', data);
    return response.data;
  },

  /**
   * Export corpus words as JSON or TXT
   */
  async exportWords(params?: {
    sectorId?: number;
    format?: 'json' | 'txt';
  }): Promise<Blob> {
    const response = await api.get('/corpus/words/export', {
      params,
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get corpus statistics
   */
  async getCorpusStats(): Promise<CorpusStats> {
    const response = await api.get<CorpusStats>('/corpus/stats');
    return response.data;
  },

  /**
   * Helper function to download exported file
   */
  downloadExportedFile(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  },
};
